import styled, { css } from 'styled-components';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { gridConfig, VideoPlayer } from '@tvg/fuse-design';
import OverlayImage from './assets/thumbnail-overlay.png';
import '@splidejs/splide/dist/css/splide.min.css';

export const OuterContainer = styled.div`
  margin: 0 calc(var(--spacing-space16) * -1);

  @media (min-width: ${gridConfig.m.minWidth}) {
    margin: 0;
  }
`;

export const InnerContainer = styled.div`
  width: 100%;
  margin-bottom: var(--spacing-space16);

  .image-zoomer {
    position: relative;
    overflow: hidden;
    display: block;
    cursor: zoom-in;
    touch-action: manipulation;
    user-select: none;
    -webkit-touch-callout: none;
    -webkit-user-drag: none;
  }

  .image-zoomer--active {
    cursor: move;
  }

  .image-zoomer__container {
    position: absolute;
    left: 0;
    top: 0;
  }

  .image-zoomer__image {
    position: absolute;
    height: 100%;
    width: 100%;
  }

  @media (min-width: ${gridConfig.m.minWidth}) {
    margin-bottom: 0;
  }

  @media (min-width: ${gridConfig.l.minWidth}) {
    display: grid;
    align-items: start;
    gap: var(--spacing-space16);
    grid-template-columns: repeat(8, 1fr);
  }
`;

export const MainCarousel = styled(Splide)`
  visibility: visible !important; // Splide sets this to hidden by default which causes issues when Splide is disabled
  width: 100%;
  margin-bottom: var(--spacing-space8);

  .splide__track {
    padding: 0 var(--spacing-space24) !important;
  }

  .splide__slide {
    margin-right: var(--spacing-space8) !important;
    width: calc(((100% + var(--spacing-space8)) / 1) - var(--spacing-space8));
  }

  @media (min-width: ${gridConfig.m.minWidth}) {
    .splide__track {
      padding: 0 !important;
    }

    .splide__slide {
      margin-right: 0 !important;
    }
  }

  @media (min-width: ${gridConfig.l.minWidth}) {
    grid-column: span 7;
    margin-bottom: 0;
    order: 1;
  }

  /* The below CSS prevents the splide slides from stacking whilst the page is loading.
   * We need this to avoid negatively impacted our CLS score.
   */

  &:not(.is-initialized) {
    .splide__slide:not(:first-child),
    .splide__arrows {
      display: none;
    }

    img {
      display: block;
    }
  }
`;

export const ThumbnailCarouselContainer = styled.div``;

export const ThumbnailCarousel = styled(Splide)`
  display: none; // Hidden by default because we only want to display the thumbnails on grid breakpoints >= medium
  visibility: visible !important; // Splide sets this to hidden by default which causes issues when Splide is disabled
  overflow: hidden;

  &.is-disabled {
    pointer-events: none;
  }

  .splide__list {
    display: flex !important;
  }

  .splide__slide {
    width: 53px !important;
    height: 71px !important;
    cursor: pointer;
    background-color: var(--color-global-grey200) !important;
    border: 1px solid var(--color-global-grey400) !important;
    margin-right: var(--spacing-space8);
    position: relative;

    &.is-active {
      border-bottom: 2px solid var(--color-brand-mid) !important;
    }
  }

  @media (min-width: ${gridConfig.m.minWidth}) {
    display: block;
  }

  @media (min-width: ${gridConfig.l.minWidth}) {
    grid-column: span 1;
    order: 0;

    .splide__list {
      flex-direction: column;
    }

    .splide__slide {
      width: auto !important;
      height: auto !important;
      margin-right: 0;
      margin-bottom: var(--spacing-space16);
    }
  }

  &:not(.is-initialized) {
    img {
      display: block;
    }
  }
`;

export const Slide = styled(SplideSlide)`
  aspect-ratio: 0.75;
`;

export const ArrowsContainer = styled.div`
  display: none; // Hidden by default because we only want to display the arrows on large grid breakpoints
  visibility: hidden; // Hidden by default so that the arrows don't appear when Splide is disabled.

  @media (min-width: ${gridConfig.m.minWidth}) {
    .is-active & {
      visibility: visible;
    }

    display: block;
  }
`;

const baseArrowStyles = css`
  && {
    width: 48px;
    height: 48px;
    opacity: 1;
    border-radius: 4px;
    background-color: #ffffff;
    box-shadow:
      0px 3px 6px rgba(0, 0, 0, 0.15),
      0px 2px 4px rgba(0, 0, 0, 0.12);
    z-index: auto;

    &:hover {
      opacity: 1;
    }

    &:disabled {
      opacity: 0;
      pointer-events: none;
    }

    svg {
      transform: none;
    }
  }
`;

export const PreviousArrow = styled.button`
  ${baseArrowStyles}
  left: 16px;
`;

export const NextArrow = styled.button`
  ${baseArrowStyles}
  right: 16px;
`;

export const PlayOverlay = styled.span`
  width: 100%;
  height: 100%;
  position: absolute;
  background: url(${OverlayImage});
  background-position: -5% -15%;
  background-size: 250%;
  background-repeat: no-repeat;
  right: 0;
  top: 0;
  z-index: 1;
  bottom: 0;
  content: '';
  left: 0;
`;

export const StyledVideoPlayer = styled(VideoPlayer)`
  display: flex;
  height: 100%;
`;
